import { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import './ShopNav.css'
import CartModal from '../CartModal'
import { useSelector, useDispatch } from 'react-redux'
import { setCartItems } from '../../store/cartSlice'
import { loginSuccess, logout } from '../../store/authSlice'
import { setWishlist } from '../../store/wishlistSlice'
import TrmValue from '../trm/Trm'
import { ProductImage } from '../shop/ProductImageGallery'

function ShopNav() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [isInitializing, setIsInitializing] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showCart, setShowCart] = useState(false)

  const cartItems = useSelector(state => state.cart.items)
  const cartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0)

  const isLoggedIn = useSelector(s => s.auth.isAuthenticated)
  const userName = useSelector(s => s.auth.user?.name || 'Mi Cuenta')
  const userEmail = useSelector(s => s.auth.user?.email || '')
  const shortName = userName.length > 15 ? userName.slice(0, 15) + '…' : userName
  const wishlistItems = useSelector(state => state.wishlist.items)

  const collapseRef = useRef(null)
  const togglerRef = useRef(null)

  const toggleMenu = () => {
    const collapse = collapseRef.current
    const toggler = togglerRef.current

    if (collapse && toggler) {
      const isOpen = collapse.classList.contains('show')
      if (isOpen) {
        collapse.classList.remove('show')
        toggler.setAttribute('aria-expanded', 'false')
      } else {
        collapse.classList.add('show')
        toggler.setAttribute('aria-expanded', 'true')
      }
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    const trimmed = searchTerm.trim()
    if (trimmed) {
      navigate(`/buscar?q=${encodeURIComponent(trimmed)}`)
    }
  }

  useEffect(() => {
    const savedWishlist = JSON.parse(localStorage.getItem('wishlist')) || []
    dispatch(setWishlist(savedWishlist))
  }, [dispatch])

  useEffect(() => {
    if (!isLoggedIn) {
      const token = localStorage.getItem('token')
      const user = (() => {
        try {
          const str = localStorage.getItem('user')
          return str && str !== 'undefined' ? JSON.parse(str) : null
        } catch {
          return null
        }
      })()

      if (token && user) {
        dispatch(loginSuccess({ token, user }))
      } else {
        dispatch(logout())
      }
    }

    const savedCart = JSON.parse(localStorage.getItem('cart')) || []
    dispatch(setCartItems(savedCart))
    setIsInitializing(false)
  }, [dispatch, isLoggedIn])

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems))
  }, [cartItems])

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    dispatch(logout())
    document.querySelector('#userDropdown')?.click()
    navigate('/tienda')
  }

  if (isInitializing) return null

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-light shop-main-nav sticky-top py-2">
        <div className="container-fluid px-3 px-md-5 shop-nav-container">

          {/* Logo */}
          <Link to="/" className="navbar-brand shop-logo-hover">
            <img src="/images/VPA.png" alt="Logo" className="shop-logo img-fluid" />
          </Link>

          {/* Carrito visible siempre en mobile */}
          <div className="d-lg-none ms-auto me-2">
            <button
              className="btn btn-cart-icon position-relative"
              type="button"
              onClick={() => setShowCart(true)}
            >
              <i className="bi bi-cart3 fs-4"></i>
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </button>
          </div>

          {/* Botón hamburguesa */}
          <button
            className="navbar-toggler"
            type="button"
            ref={togglerRef}
            onClick={toggleMenu}
            aria-label="Toggle navigation"
            aria-expanded="false"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          {/* Menú colapsable */}
          <div
            className="collapse navbar-collapse shop-main-collapse"
            id="shopNavbar"
            ref={collapseRef}
          >
            {/* ===== MOBILE MENU ===== */}
            <div className="mobile-menu d-lg-none">

              {/* Row 1: Buscador */}
              <div className="mobile-row mb-3">
                <form className="input-group" onSubmit={handleSearch}>
                  <input
                    type="search"
                    className="form-control rounded-start"
                    placeholder="¿Qué estás buscando?"
                    aria-label="Buscar productos"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button
                    className="btn btn-outline-secondary rounded-end"
                    aria-label="Buscar"
                    type="submit"
                  >
                    <i className="bi bi-search"></i>
                  </button>
                </form>
              </div>

              {/* Row 2: Enlaces principales */}
              <div className="mobile-row d-flex justify-content-around align-items-center mb-3">

                {/* Casa */}
                <Link to="/tienda" className="btn btn-link p-0" aria-label="Ir a tienda">
                  <i className="bi bi-house-door fs-4"></i>
                </Link>

                {/* Wishlist */}
                <div className="dropdown">
                  <button
                    className="btn position-relative"
                    type="button"
                    id="wishlistDropdownMobile"
                    data-bs-toggle="dropdown"
                    aria-label="Lista de deseos"
                  >
                    <i className="bi bi-heart fs-4"></i>
                    {wishlistItems.length > 0 && (
                      <span className="cart-badge">{wishlistItems.length}</span>
                    )}
                  </button>
                  <ul
                    className="dropdown-menu dropdown-menu-end p-2"
                    aria-labelledby="wishlistDropdownMobile"
                    style={{ minWidth: '250px' }}
                  >
                    {wishlistItems.length === 0 ? (
                      <li className="text-muted px-2">Tu lista está vacía</li>
                    ) : (
                      <>
                        {wishlistItems.slice(0, 3).map(item => (
                          <li key={item.id} className="d-flex align-items-center mb-2 px-2">
                            <ProductImage
                              imageUrl={item.imageUrl}
                              alt={item.name}
                              style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                              className="me-2 rounded"
                            />
                            <span className="text-truncate" style={{ maxWidth: '150px' }}>{item.name}</span>
                          </li>
                        ))}
                        <li><hr className="dropdown-divider" /></li>
                        <li><Link to="/wishlist" className="dropdown-item text-primary">Ver todos</Link></li>
                      </>
                    )}
                  </ul>
                </div>

                {/* TRM */}
                <div className="shop-trm-indicator shop-trm-indicator-mobile">
                  <TrmValue />
                </div>

                {/* Soporte */}
                <div className="dropdown">
                  <button
                    className="btn btn-sm btn-light dropdown-toggle text-dark fw-medium"
                    type="button"
                    id="supportDropdownMobile"
                    data-bs-toggle="dropdown"
                    aria-label="Soporte"
                  >
                    <i className="bi bi-headset me-1"></i>
                  </button>
                  <ul className="dropdown-menu" aria-labelledby="supportDropdownMobile">
                    <li><a className="dropdown-item" href="#"><i className="bi bi-question-circle me-2"></i>Ayuda</a></li>
                    <li><a className="dropdown-item" href="#"><i className="bi bi-chat-dots me-2"></i>Chat</a></li>
                    <li><a className="dropdown-item" href="/contacto"><i className="bi bi-telephone me-2"></i>Contacto</a></li>
                  </ul>
                </div>
              </div>

              {/* Row 3: Usuario y carrito */}
              <div className="mobile-row d-flex justify-content-around align-items-center">

                {/* Usuario */}
                <div className="dropdown">
                  <button
                    className="btn btn-sm btn-light dropdown-toggle text-dark fw-medium d-flex align-items-center"
                    type="button"
                    id="userDropdownMobile"
                    data-bs-toggle="dropdown"
                    aria-label="Opciones de cuenta"
                  >
                    <i className="bi bi-person-circle fs-4 me-2"></i>
                    <div className="d-flex flex-column text-start">
                      <span className="fw-medium">{isLoggedIn ? shortName : 'Mi Cuenta'}</span>
                      {isLoggedIn && <span className="small text-muted">{userEmail}</span>}
                    </div>
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdownMobile">
                    {isLoggedIn ? (
                      <>
                        <li><a className="dropdown-item" href="/mi-cuenta"><i className="bi bi-gear me-2"></i>Mi cuenta</a></li>
                        <li><hr className="dropdown-divider" /></li>
                        <li>
                          <button className="dropdown-item text-danger" onClick={handleLogout}>
                            <i className="bi bi-box-arrow-right me-2"></i>Cerrar sesión
                          </button>
                        </li>
                      </>
                    ) : (
                      <>
                        <li><a className="dropdown-item" href="/loginVpStore"><i className="bi bi-box-arrow-in-right me-2"></i>Iniciar Sesión</a></li>
                        <li><a className="dropdown-item" href="/registerVpStore"><i className="bi bi-person-plus me-2"></i>Registrarse</a></li>
                      </>
                    )}
                  </ul>
                </div>

                {/* Carrito escritorio (visible solo mobile en este contenedor) */}
                {/* <div>
                <button
                  className="btn btn-cart-icon position-relative"
                  type="button"
                  id="cartDropdownMobile"
                  data-bs-toggle="dropdown"
                  aria-label="Carrito de compras"
                >
                  <i className="bi bi-cart3 fs-4"></i>
                  {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
                </button>
                <CartDropdown />
              </div> */}
              </div>
            </div>

            {/* ===== DESKTOP MENU ===== */}
            <div className="desktop-menu d-none d-lg-flex w-100 justify-content-between align-items-center mt-3 mt-lg-0">

              {/* Buscador */}
              <div className="col-12 col-lg-6 mb-3 mb-lg-0">
                <form className="input-group" onSubmit={handleSearch}>
                  <input
                    type="search"
                    className="form-control rounded-start"
                    placeholder="¿Qué estás buscando?"
                    aria-label="Buscar productos"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button className="btn btn-outline-secondary rounded-end" aria-label="Buscar" type="submit">
                    <i className="bi bi-search"></i>
                  </button>
                </form>
              </div>

              {/* Enlaces e iconos */}
              <div className="d-flex align-items-center position-relative me-3" style={{ gap: '8px' }}>

                {/* Casa */}
                <Link to="/tienda" className="btn btn-link p-0" aria-label="Ir a tienda" style={{ lineHeight: 1 }}>
                  <i className="bi bi-house-door fs-4"></i>
                </Link>

                {/* Wishlist */}
                <button
                  className="btn position-relative"
                  type="button"
                  id="wishlistDropdown"
                  data-bs-toggle="dropdown"
                  aria-label="Lista de deseos"
                >
                  <i className="bi bi-heart fs-4"></i>
                  {wishlistItems.length > 0 && (
                    <span className="cart-badge">{wishlistItems.length}</span>
                  )}
                </button>
                <ul className="dropdown-menu dropdown-menu-end p-2" aria-labelledby="wishlistDropdown" style={{ minWidth: '250px' }}>
                  {wishlistItems.length === 0 ? (
                    <li className="text-muted px-2">Tu lista está vacía</li>
                  ) : (
                    <>
                      {wishlistItems.slice(0, 3).map(item => (
                        <li key={item.id} className="d-flex align-items-center mb-2 px-2">
                          <ProductImage imageUrl={item.imageUrl} alt={item.name} style={{ width: '40px', height: '40px', objectFit: 'cover' }} className="me-2 rounded" />
                          <span className="text-truncate" style={{ maxWidth: '150px' }}>{item.name}</span>
                        </li>
                      ))}
                      <li><hr className="dropdown-divider" /></li>
                      <li><Link to="/wishlist" className="dropdown-item text-primary">Ver todos</Link></li>
                    </>
                  )}
                </ul>

                {/* TRM */}
                <div className="d-flex align-items-center shop-trm-indicator shop-trm-indicator-desktop">
                  <TrmValue />
                </div>

                {/* Soporte */}
                <div className="dropdown">
                  <button
                    className="btn btn-sm btn-light dropdown-toggle text-dark fw-medium"
                    type="button"
                    id="supportDropdown"
                    data-bs-toggle="dropdown"
                    aria-label="Soporte"
                  >
                    <i className="bi bi-headset me-1"></i> Soporte
                  </button>
                  <ul className="dropdown-menu">
                    <li><a className="dropdown-item" href="#"><i className="bi bi-question-circle me-2"></i>Ayuda</a></li>
                    <li><a className="dropdown-item" href="#"><i className="bi bi-chat-dots me-2"></i>Chat</a></li>
                    <li><a className="dropdown-item" href="/contacto"><i className="bi bi-telephone me-2"></i>Contacto</a></li>
                  </ul>
                </div>
              </div>

              {/* Usuario + carrito */}
              <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3">

                {/* Usuario */}
                <div className="dropdown">
                  <button
                    className="btn btn-sm btn-light dropdown-toggle text-dark fw-medium"
                    type="button"
                    id="userDropdown"
                    data-bs-toggle="dropdown"
                    aria-label="Opciones de cuenta"
                  >
                    <div className="d-flex align-items-center">
                      <i className="bi bi-person-circle fs-4 me-2"></i>
                      <div className="d-flex flex-column text-start">
                        <span className="fw-medium">{isLoggedIn ? shortName : 'Mi Cuenta'}</span>
                        {isLoggedIn && <span className="small text-muted">{userEmail}</span>}
                      </div>
                    </div>
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    {isLoggedIn ? (
                      <>
                        <li><a className="dropdown-item" href="/mi-cuenta"><i className="bi bi-gear me-2"></i>Mi cuenta</a></li>
                        <li><hr className="dropdown-divider" /></li>
                        <li>
                          <button className="dropdown-item text-danger" onClick={handleLogout}>
                            <i className="bi bi-box-arrow-right me-2"></i>Cerrar sesión
                          </button>
                        </li>
                      </>
                    ) : (
                      <>
                        <li><a className="dropdown-item" href="/loginVpStore"><i className="bi bi-box-arrow-in-right me-2"></i>Iniciar Sesión</a></li>
                        <li><a className="dropdown-item" href="/registerVpStore"><i className="bi bi-person-plus me-2"></i>Registrarse</a></li>
                      </>
                    )}
                  </ul>
                </div>

                {/* Carrito escritorio */}
                <div className="d-none d-lg-block position-relative">
                  <button
                    className="btn btn-cart-icon position-relative"
                    type="button"
                    onClick={() => setShowCart(true)}
                  >
                    <i className="bi bi-cart3 fs-4"></i>
                    {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </nav>
      <CartModal
        show={showCart}
        onClose={() => setShowCart(false)}
      />
    </>
  )
}

export default ShopNav
